/**
 * Router root — rutas top-level de la app.
 *
 * Estructura:
 *   - /login                    → LoginPage (público)
 *   - /                         → redirect a /dashboard si autenticado, /login si no
 *   - /dashboard, /despacho...  → wrapped en <ProtectedRoute> + AppShell
 *   - /reportes/*, /maestros/*, /seguridad/* → shells con sub-rutas (paquete L)
 *
 * Las rutas de feature se cargan por ahora estáticamente; cuando crezca el
 * bundle se puede usar `React.lazy()` por feature.
 */

import { createBrowserRouter, Navigate, RouterProvider } from 'react-router-dom';
import { LoginPage } from '@/features/auth/LoginPage';
import { ProtectedRoute } from '@/features/auth/ProtectedRoute';
import { AppShell } from '@/components/layout/AppShell';
import { DashboardPage } from '@/features/dashboard/DashboardPage';
import { DespachoPage } from '@/features/despacho/DespachoPage';
import { RecepcionPage } from '@/features/recepcion/RecepcionPage';
import { AuditPage } from '@/features/audit/AuditPage';
import { ApiLogPage } from '@/features/apilog/ApiLogPage';
import { SeriesPage } from '@/features/series/SeriesPage';
import { IngresoPage } from '@/features/ingreso/IngresoPage';
import { LotesPage } from '@/features/lotes/LotesPage';
import { ManualPage } from '@/features/manual/ManualPage';
import { HandoffPage } from '@/features/handoff/HandoffPage';
import { AnmatPage } from '@/features/anmat/AnmatPage';
import { ConsultaGlnPage } from '@/features/snt/ConsultaGlnPage';
import { TrazabilidadPage } from '@/features/trazabilidad/TrazabilidadPage';
import { OperacionDetallePage } from '@/features/operaciones/OperacionDetallePage';
// Reportes con sub-rutas
import { ReportesShell } from '@/features/reportes/ReportesShell';
import { MovimientosTab } from '@/features/reportes/tabs/MovimientosTab';
import { CruceTab } from '@/features/reportes/tabs/CruceTab';
import { DestruccionesTab } from '@/features/reportes/tabs/DestruccionesTab';
// Maestros con sub-rutas
import { MaestrosShell } from '@/features/maestros/MaestrosShell';
import { ProductosTab } from '@/features/maestros/tabs/ProductosTab';
import { ClientesTab } from '@/features/maestros/tabs/ClientesTab';
import { EventosTab } from '@/features/maestros/tabs/EventosTab';
// Seguridad con sub-rutas
import { SeguridadShell } from '@/features/seguridad/SeguridadShell';
import { UsuariosPage } from '@/features/seguridad/UsuariosPage';
import { RolesPage } from '@/features/seguridad/RolesPage';
import { SesionesPage } from '@/features/seguridad/SesionesPage';
import { PoliticaSesionPage } from '@/features/seguridad/PoliticaSesionPage';

/**
 * Basename para React Router cuando la app vive en un sub-path (ej. /admin).
 * Vite expone el valor de `base` (de vite.config.ts) como
 * `import.meta.env.BASE_URL` con barra final. React Router espera el path
 * sin barra final — sacarla. Caso default '/' → basename undefined.
 */
const routerBasename = (() => {
  const raw = import.meta.env.BASE_URL ?? '/';
  if (raw === '/' || raw === '') return undefined;
  return raw.endsWith('/') ? raw.slice(0, -1) : raw;
})();

const router = createBrowserRouter(
  [
  {
    path: '/login',
    element: <LoginPage />,
  },
  {
    path: '/',
    element: (
      <ProtectedRoute>
        <AppShell />
      </ProtectedRoute>
    ),
    children: [
      { index: true, element: <Navigate to="/dashboard" replace /> },
      { path: 'dashboard', element: <DashboardPage /> },
      { path: 'despacho', element: <DespachoPage /> },
      { path: 'recepcion', element: <RecepcionPage /> },
      { path: 'audit', element: <AuditPage /> },
      { path: 'apilog', element: <ApiLogPage /> },
      { path: 'series', element: <SeriesPage /> },
      { path: 'ingreso', element: <IngresoPage /> },
      { path: 'lotes', element: <LotesPage /> },
      { path: 'anmat', element: <AnmatPage /> },
      { path: 'consulta-gln', element: <ConsultaGlnPage /> },
      { path: 'trazabilidad', element: <TrazabilidadPage /> },
      { path: 'operaciones/:opId', element: <OperacionDetallePage /> },
      // Reportes con sub-rutas
      {
        path: 'reportes',
        element: <ReportesShell />,
        children: [
          { index: true, element: <Navigate to="movimientos" replace /> },
          { path: 'movimientos', element: <MovimientosTab /> },
          { path: 'cruce', element: <CruceTab /> },
          { path: 'destrucciones', element: <DestruccionesTab /> },
        ],
      },
      // Maestros con sub-rutas
      {
        path: 'maestros',
        element: <MaestrosShell />,
        children: [
          { index: true, element: <Navigate to="productos" replace /> },
          { path: 'productos', element: <ProductosTab /> },
          { path: 'clientes', element: <ClientesTab /> },
          { path: 'eventos', element: <EventosTab /> },
        ],
      },
      // Seguridad con sub-rutas
      {
        path: 'seguridad',
        element: <SeguridadShell />,
        children: [
          { index: true, element: <Navigate to="usuarios" replace /> },
          { path: 'usuarios', element: <UsuariosPage /> },
          { path: 'roles', element: <RolesPage /> },
          { path: 'sesiones', element: <SesionesPage /> },
          { path: 'politica', element: <PoliticaSesionPage /> },
        ],
      },
      { path: 'manual', element: <ManualPage /> },
      { path: 'handoff', element: <HandoffPage /> },
    ],
  },
  // Fallback — cualquier ruta desconocida vuelve a la home
  { path: '*', element: <Navigate to="/" replace /> },
  ],
  { basename: routerBasename },
);

export function AppRouter() {
  return <RouterProvider router={router} />;
}
